import moment, { Moment } from "moment";
import { ActivityLog, Board, ActivityLogColorValue, ActivityLogUpdateColumnValueData, ItemValue, Labels } from "../types";
import { processFormula } from "./formulas";

export enum TimelineEventType {
    CREATE,
    UPDATE,
    DELETE
}
export type TimelineEvent = {
    date: Moment;
    item: string;
    event: TimelineEventType;
    column: string; // column id for which the value is changing
    value: any; // actual value, e.g. number of label (for columns of type "status")
    dt?: number; // time to next event in ms

    prevEvent?: TimelineEvent;
    nextEvent?: TimelineEvent;
};

type ItemMap = { [id: string]: { [columnId: string]: any } };
export type Bucket = { [itemId: string]: number };
export type Buckets = { [labelId: string]: Bucket };

export type BoardState = {
    date: Moment;
    items: ItemMap; // map id to dict of some column values
    lastEvents: { [itemId: string]: TimelineEvent }; // map id to last event which occurred for item
    buckets: Buckets; // dict of itemIds -> position per each label
    creationLogs?: { itemId: string; createdAt: Moment }[]; // creation events sorted in desc order
};


/**
 * from Alex Savchuk:
 *
 * Hmm, that’s a fair point. That said, the Status columns will only be set to a “null” value if:
 *  - The item was created without any manual Status selection within the board;
 *  - The item was created through a Form, and the user didn’t select any value;
 * 
 * In those cases, the UI will always default to the Gray label. Since that’s the case, we could check
 * the settings_str of a Status column for the 5th index (Gray label) and see if that has any text,
 * since Status columns will always default to the Gray label if there are no other selections made.
 * 
 */
const HACK_DEFAULT_STATUS_ID = "5";

/**
 * Normalise item values to simpler dict
 */
export function itemsToBoardState(board: Board, date: Moment, labels?: Labels): BoardState {
    const itemMap: ItemMap = {};
    const buckets: Buckets = {};
    const creationLogs: { itemId: string; createdAt: Moment }[] = [];

    // cache any formulas
    const formulas: { [columnId: string]: string } = {};
    board.columns
        .filter((column) => column.type === "formula")
        .forEach((formulaColumn) => {
            const settingsStr = JSON.parse(formulaColumn.settings_str);
            formulas[formulaColumn.id] = settingsStr.formula;
        });

    // parse values for every column
    board.items.forEach((item) => {
        const values: { [columnId: string]: any } = {};
        const formulaValues: ItemValue[] = [];
        const valuesForFormulas: { [columnId: string]: any } = {};
        item.column_values.forEach((value) => {
            if (value.valueJson !== undefined) {
                const parsedValue = value.valueJson;
                if (value.type === "status") {
                    values[value.id] = parsedValue.index;
                    valuesForFormulas[value.id] = `"${value?.label ? value.label : ""}"`;
                } else if (value.type === "numbers") {
                    values[value.id] = parseFloat(parsedValue);
                    valuesForFormulas[value.id] = values[value.id];
                } else if (value.type === "date") {
                    values[value.id] = moment(parsedValue.date);
                    valuesForFormulas[value.id] = `"${parsedValue.date}"`;
                } else {
                    values[value.id] = parsedValue;
                    valuesForFormulas[value.id] = `"${parsedValue}"`;
                }
            } else {
                if (value.type === "formula") {
                    formulaValues.push(value);
                } else if (value.type === "status") {
                    // HACK: until Monday fixes returning column_value.value for new statuses
                    if (value.text !== null) {
                        values[value.id] = HACK_DEFAULT_STATUS_ID;
                    }
                } else if (value.type === "numbers") {
                    values[value.id] = 0;
                    valuesForFormulas[value.id] = values[value.id];
                } else if (value.type === "date") {
                    values[value.id] = moment(0);
                    valuesForFormulas[value.id] = 0;
                } else {
                    values[value.id] = "";
                    valuesForFormulas[value.id] = `""`;
                }
            }
        });

        // calculate formulas once all values in the row have been parsed
        formulaValues.forEach((formulaValue) => {
            if (formulas[formulaValue.id] !== undefined) {
                values[formulaValue.id] = processFormula(formulas[formulaValue.id], valuesForFormulas);
            } else {
                console.warn(
                    `Recursive formulas are not supported for now. Formula "${formulaValue.id}" is used within another formula and cannot be evaluated.`
                );
                values[formulaValue.id] = "";
            }
        });
        itemMap[item.id] = values;
        creationLogs.push({ itemId: item.id, createdAt: moment.utc(item.created_at, "YYYY-MM-DD HH:mm:ss") });
    });

    // sort in desc order
    creationLogs.sort((a, b) => b.createdAt.valueOf() - a.createdAt.valueOf());

    return {
        date,
        items: itemMap,
        buckets,
        lastEvents: {},
        creationLogs
    };
}

const activityLogValueParsers: { [type: string]: (value: null | ActivityLogColorValue) => any } = {
    // HACK: until Monday fixes returning column_value.value for new statuses
    status: (value) => (value ? value.label.index + "" : HACK_DEFAULT_STATUS_ID)
};

export function copyBoard(board: BoardState): BoardState {
    const items: any = {};
    for (const itemId in board.items) {
        items[itemId] = { ...board.items[itemId] };
    }

    const buckets: Buckets = {};
    for (const labelId in board.buckets) {
        buckets[labelId] = { ...board.buckets[labelId] };
    }

    return {
        date: board.date.clone(),
        items,
        buckets,
        lastEvents: { ...board.lastEvents }
    };
}

/**
 * @param endBoard current state of the board
 * @param activityLogs historical logs, have to be ordered from newest to oldest
 * @param columnId column id to produce events for
 * @param columnType required to know how to parse value
 */
export function getEvents(
    endBoard: BoardState,
    activityLogs: ActivityLog[] | null,
    columnId: string,
    columnType: "status"
): { startBoard: BoardState; events: TimelineEvent[] } {
    const events: TimelineEvent[] = [];
    const startBoard: BoardState = copyBoard(endBoard);

    // used to get delta time to next event
    const nextEventPerItem: { [itemId: string]: TimelineEvent } = {};

    // generate creation events log - desc ordered
    const creationLogs = endBoard.creationLogs || [];
    let creationLogsIdx = 0;

    const itemsWithCreateEvent = new Set<string>();

    const scanCreationLogs = (until: Moment): void => {
        while (creationLogsIdx < creationLogs.length && creationLogs[creationLogsIdx].createdAt.isSameOrAfter(until)) {
            const clog = creationLogs[creationLogsIdx];

            // if there's no other "CREATE" event, and item has some state assigned,
            // take the latest known state for this item and assume it got created with it
            if (
                !itemsWithCreateEvent.has(clog.itemId) &&
                startBoard.items[clog.itemId] &&
                startBoard.items[clog.itemId][columnId] !== undefined
            ) {
                const event: TimelineEvent = {
                    date: clog.createdAt,
                    item: clog.itemId,
                    event: TimelineEventType.CREATE,
                    column: columnId,
                    value: startBoard.items[clog.itemId][columnId] + ""
                };
                delete startBoard.items[clog.itemId];
                events.unshift(event);

                const nextEvent = nextEventPerItem[event.item];
                if (nextEvent) {
                    event.dt = nextEvent.date.diff(event.date);
                    // make it a linked list
                    event.nextEvent = nextEvent;
                    nextEvent.prevEvent = event;
                }
                nextEventPerItem[event.item] = event;

                itemsWithCreateEvent.add(event.item);
            }
            creationLogsIdx++;
        }
    };

    // iterate from latest to oldest event (going towards past)
    if (activityLogs !== null) {
        for (let i = 0; i < activityLogs.length; i++) {
            const log = activityLogs[i];
            const logCreatedAt: Moment = moment.utc(log.created_at / 1e4); // log.created_at is in UTC

            scanCreationLogs(logCreatedAt);

            if (log.event === "update_column_value") {
                const data: ActivityLogUpdateColumnValueData = log.dataJson!;
                if (!startBoard.items[data.pulse_id + ""]) {
                    continue;
                }

                if (data.column_id === columnId) {
                    const event: Partial<TimelineEvent> = {
                        date: logCreatedAt,
                        item: data.pulse_id + "",
                        column: columnId
                    };
                    event.value = activityLogValueParsers[columnType](data.value);
                    event.event = TimelineEventType.UPDATE;

                    // calculate delta time to next event
                    const nextEvent = nextEventPerItem[event.item!];
                    if (nextEvent) {
                        event.dt = nextEvent.date.diff(event.date);
                        // make it a linked list
                        event.nextEvent = nextEvent;
                        nextEvent.prevEvent = event as TimelineEvent;
                    }
                    nextEventPerItem[event.item!] = event as TimelineEvent;

                    // keep track of board state
                    startBoard.items[event.item!][columnId] = activityLogValueParsers[columnType](
                        data.previous_value
                    );

                    events.unshift(event as TimelineEvent);
                }
            }
        }
    }

    // scan all the remaining create events
    scanCreationLogs(moment(1));

    if (events.length) {
        startBoard.date = events[0].date;
    }

    return { startBoard, events };
}

/**
 * Take values of statuses from columnId for each item and create buckets of items per status
 */
export function initBoardBuckets(labelIds: string[], board: BoardState, columnId: string): void {
    const buckets: Buckets = {};
    board.buckets = buckets;

    labelIds.forEach((labelId) => (buckets[labelId] = {} as Bucket));

    for (const itemId in board.items) {
        const labelId: string = board.items[itemId][columnId];
        if (labelId !== undefined) {
            buckets[labelId][itemId] = 0;
        }
    }
}

/**
 * Progress state of board time ms
 * @returns eventIdx: first unconsumed event id
 * modifiedValues: statuses with new items in them - they need sorting
 * modifiedItems: items which changed statuses - original status
 */
export function updateBoard(
    board: BoardState,
    events: TimelineEvent[],
    currentEventIdx: number,
    targetTime: Moment,
    columnId: string
): {
    eventIdx: number;
    itemsMovedFrom: { [itemId: string]: string | null };
    addedCount: { [labelId: string]: number };
    removedCount: { [labelId: string]: number };
} {
    const itemsMovedFrom: { [itemId: string]: string | null } = {};
    const addedCount: { [labelId: string]: number } = {};
    const removedCount: { [labelId: string]: number } = {};

    if (!events || !events.length || currentEventIdx >= events.length) {
        return { eventIdx: currentEventIdx, itemsMovedFrom, addedCount, removedCount };
    }
    if (events[currentEventIdx].date.isBefore(board.date)) {
        throw new Error("First event earlier than board state");
    }
    if (targetTime.isBefore(board.date)) {
        return { eventIdx: 0, itemsMovedFrom, addedCount, removedCount };
        // throw new Error("Going back in time not supported");
    }

    const { items, lastEvents } = board;
    let i;
    for (i = currentEventIdx; i < events.length && events[i].date.isSameOrBefore(targetTime); i++) {
        const e = events[i];
        if (board.date.isAfter(e.date)) {
            // skip events from before state of board
            continue;
        }

        if (e.column === columnId) {
            const newStatus = e.value as string;

            addedCount[newStatus] = addedCount[newStatus] || 0;
            addedCount[newStatus] += 1;

            // store original old status for the item
            if (itemsMovedFrom[e.item] === undefined) {
                itemsMovedFrom[e.item] = items[e.item] !== undefined ? items[e.item][columnId] : null;
            }
            // keep buckets up to date
            if (items[e.item] !== undefined) {
                const oldStatus = items[e.item][columnId];
                delete board.buckets[oldStatus][e.item];
                removedCount[oldStatus] = removedCount[oldStatus] || 0;
                removedCount[oldStatus] += 1;
            }
            board.buckets[newStatus][e.item] = e.nextEvent ? e.nextEvent.date.valueOf() : 1e18;
        }

        if (e.event === TimelineEventType.CREATE) {
            items[e.item] = {};
        }

        const boardItem = items[e.item];

        if (boardItem) {
            boardItem[e.column] = e.value;
        }

        lastEvents[e.item] = e;
        board.date = e.date;
    }

    board.date = targetTime.clone();

    return { eventIdx: i, itemsMovedFrom, addedCount, removedCount };
}
